import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus, Pencil, Trash2, X, KeyRound, ShieldCheck, Copy, Check,
} from 'lucide-react'
import {
  backofficeApi,
  type BackofficeUser,
  type BackofficeOrg,
  type BackofficeCreateUserPayload,
  type BackofficeUpdateUserPayload,
} from '../../lib/api'

const ROLE_COLORS: Record<string, string> = {
  admin: 'bg-emerald-900/50 text-emerald-300',
  editor: 'bg-blue-900/50 text-blue-300',
  viewer: 'bg-slate-700 text-slate-300',
}

// ---------------------------------------------------------------------------
// Create user modal
// ---------------------------------------------------------------------------

interface CreateUserModalProps {
  orgs: BackofficeOrg[]
  onClose: () => void
  onSave: (payload: BackofficeCreateUserPayload) => void
  saving: boolean
}

function CreateUserModal({ orgs, onClose, onSave, saving }: CreateUserModalProps) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [fullName, setFullName] = useState('')
  const [orgId, setOrgId] = useState('')
  const [role, setRole] = useState('editor')
  const [isSuperAdmin, setIsSuperAdmin] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const payload: BackofficeCreateUserPayload = {
      email,
      password,
      full_name: fullName,
      role,
      is_super_admin: isSuperAdmin,
    }
    if (orgId) payload.organization_id = orgId
    onSave(payload)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
          <h2 className="text-sm font-semibold text-slate-100">New user</h2>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-300">
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-xs text-slate-400 mb-1">Email *</label>
            <input
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-500"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Full name *</label>
            <input
              required
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-500"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">
              Password *{' '}
              <span className="text-slate-500">(user can change after first login)</span>
            </label>
            <input
              required
              type="password"
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-500"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Organization</label>
              <select
                value={orgId}
                onChange={(e) => setOrgId(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-500"
              >
                <option value="">No org</option>
                {orgs.map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Role</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-500"
              >
                <option value="admin">admin</option>
                <option value="editor">editor</option>
                <option value="viewer">viewer</option>
              </select>
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isSuperAdmin}
              onChange={(e) => setIsSuperAdmin(e.target.checked)}
              className="rounded border-slate-600 bg-slate-800 text-emerald-500 focus:ring-emerald-500"
            />
            <span className="text-xs text-slate-400">Grant super-admin (back-office access)</span>
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-slate-400 hover:text-slate-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {saving ? 'Creating…' : 'Create user'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Edit user modal
// ---------------------------------------------------------------------------

interface EditUserModalProps {
  user: BackofficeUser
  orgs: BackofficeOrg[]
  onClose: () => void
  onSave: (payload: BackofficeUpdateUserPayload) => void
  saving: boolean
}

function EditUserModal({ user, orgs, onClose, onSave, saving }: EditUserModalProps) {
  const [fullName, setFullName] = useState(user.full_name ?? '')
  const [role, setRole] = useState(user.role ?? 'editor')
  const [orgId, setOrgId] = useState(user.organization_id ?? '')
  const [isSuperAdmin, setIsSuperAdmin] = useState(user.is_super_admin)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const payload: BackofficeUpdateUserPayload = {
      full_name: fullName,
      role,
      organization_id: orgId || null,
      is_super_admin: isSuperAdmin,
    }
    onSave(payload)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
          <h2 className="text-sm font-semibold text-slate-100">Edit user</h2>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-300">
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <p className="text-xs text-slate-500">{user.email}</p>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Full name</label>
            <input
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-500"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Organization</label>
              <select
                value={orgId}
                onChange={(e) => setOrgId(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-500"
              >
                <option value="">No org</option>
                {orgs.map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Role</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-500"
              >
                <option value="admin">admin</option>
                <option value="editor">editor</option>
                <option value="viewer">viewer</option>
              </select>
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isSuperAdmin}
              onChange={(e) => setIsSuperAdmin(e.target.checked)}
              className="rounded border-slate-600 bg-slate-800 text-emerald-500 focus:ring-emerald-500"
            />
            <span className="text-xs text-slate-400">Super-admin (back-office access)</span>
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-slate-400 hover:text-slate-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Reset password result modal
// ---------------------------------------------------------------------------

function ResetLinkModal({ email, link, onClose }: { email: string; link: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    navigator.clipboard.writeText(link)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-lg p-6 space-y-4">
        <h2 className="text-sm font-semibold text-slate-100">Password reset link</h2>
        <p className="text-sm text-slate-400">
          Share this link with <span className="text-slate-200">{email}</span>. It expires after first use.
        </p>
        <div className="flex items-center gap-2 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2">
          <span className="text-xs text-slate-400 flex-1 break-all font-mono">{link}</span>
          <button
            onClick={handleCopy}
            className="text-slate-400 hover:text-emerald-400 transition-colors flex-shrink-0"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </button>
        </div>
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-slate-700 hover:bg-slate-600 text-slate-100 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function BackofficeUsersPage() {
  const qc = useQueryClient()
  const [createOpen, setCreateOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<BackofficeUser | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<BackofficeUser | null>(null)
  const [resetLink, setResetLink] = useState<{ email: string; link: string } | null>(null)
  const [search, setSearch] = useState('')

  const { data: users = [], isLoading } = useQuery<BackofficeUser[]>({
    queryKey: ['backoffice', 'users'],
    queryFn: backofficeApi.listUsers,
  })

  const { data: orgs = [] } = useQuery<BackofficeOrg[]>({
    queryKey: ['backoffice', 'orgs'],
    queryFn: backofficeApi.listOrgs,
  })

  const orgMap = Object.fromEntries(orgs.map((o) => [o.id, o.name]))

  const createMutation = useMutation({
    mutationFn: (payload: BackofficeCreateUserPayload) => backofficeApi.createUser(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['backoffice', 'users'] })
      qc.invalidateQueries({ queryKey: ['backoffice', 'stats'] })
      setCreateOpen(false)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: BackofficeUpdateUserPayload }) =>
      backofficeApi.updateUser(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['backoffice', 'users'] })
      setEditTarget(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => backofficeApi.deleteUser(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['backoffice', 'users'] })
      qc.invalidateQueries({ queryKey: ['backoffice', 'stats'] })
      setDeleteTarget(null)
    },
  })

  const resetMutation = useMutation({
    mutationFn: (id: string) => backofficeApi.resetPassword(id),
    onSuccess: (data) => {
      setResetLink({ email: data.email, link: data.reset_link })
    },
  })

  const filtered = users.filter(
    (u) =>
      (u.email ?? '').toLowerCase().includes(search.toLowerCase()) ||
      (u.full_name ?? '').toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-100">Users</h1>
          <p className="text-sm text-slate-500 mt-0.5">{users.length} total</p>
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          New user
        </button>
      </div>

      {/* Search */}
      <input
        type="text"
        placeholder="Search by email or name…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="w-full max-w-xs bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-emerald-500"
      />

      {/* Table */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-emerald-500" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-slate-500 text-sm">No users found.</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-800">
                <th className="text-left px-5 py-3 text-xs font-medium text-slate-400">User</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-slate-400">Organization</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-slate-400">Role</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-slate-400">Joined</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((user) => (
                <tr
                  key={user.id}
                  className="border-b border-slate-800 hover:bg-slate-800/40 transition-colors"
                >
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-slate-700 flex items-center justify-center text-xs font-bold text-slate-300 flex-shrink-0">
                        {(user.full_name ?? user.email ?? '?')[0].toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm text-slate-100 font-medium leading-tight">
                          {user.full_name ?? <span className="text-slate-500 italic">No name</span>}
                        </p>
                        <p className="text-xs text-slate-500">{user.email}</p>
                      </div>
                      {user.is_super_admin && (
                        <span title="Super-admin">
                          <ShieldCheck className="w-3.5 h-3.5 text-emerald-400 ml-1" />
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-sm text-slate-400">
                    {user.organization_id
                      ? (orgMap[user.organization_id] ?? (
                        <span className="font-mono text-xs">{user.organization_id.slice(0, 8)}…</span>
                      ))
                      : <span className="text-slate-600 italic">None</span>}
                  </td>
                  <td className="px-5 py-3">
                    {user.role ? (
                      <span
                        className={`text-xs font-medium px-2 py-0.5 rounded-full ${ROLE_COLORS[user.role] ?? 'bg-slate-700 text-slate-300'}`}
                      >
                        {user.role}
                      </span>
                    ) : (
                      <span className="text-slate-600 text-xs italic">—</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-xs text-slate-500">
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        onClick={() => resetMutation.mutate(user.id)}
                        disabled={resetMutation.isPending}
                        className="p-1.5 text-slate-500 hover:text-yellow-400 transition-colors"
                        title="Reset password"
                      >
                        <KeyRound className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setEditTarget(user)}
                        className="p-1.5 text-slate-500 hover:text-emerald-400 transition-colors"
                        title="Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeleteTarget(user)}
                        className="p-1.5 text-slate-500 hover:text-red-400 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create modal */}
      {createOpen && (
        <CreateUserModal
          orgs={orgs}
          onClose={() => setCreateOpen(false)}
          onSave={(payload) => createMutation.mutate(payload)}
          saving={createMutation.isPending}
        />
      )}

      {/* Edit modal */}
      {editTarget && (
        <EditUserModal
          user={editTarget}
          orgs={orgs}
          onClose={() => setEditTarget(null)}
          onSave={(payload) =>
            updateMutation.mutate({ id: editTarget.id, payload })
          }
          saving={updateMutation.isPending}
        />
      )}

      {/* Reset link modal */}
      {resetLink && (
        <ResetLinkModal
          email={resetLink.email}
          link={resetLink.link}
          onClose={() => setResetLink(null)}
        />
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-sm p-6 space-y-4">
            <h2 className="text-sm font-semibold text-slate-100">Delete user</h2>
            <p className="text-sm text-slate-400">
              Are you sure you want to delete{' '}
              <span className="text-slate-200 font-medium">
                {deleteTarget.full_name ?? deleteTarget.email}
              </span>?
              Their account will be permanently removed.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 text-sm text-slate-400 hover:text-slate-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate(deleteTarget.id)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
